// What a reply sounds like: the text handed to the TTS voice, before
// speechChunks cuts it into requests and speechAudio fetches and plays them.
//
// A reply is not plain prose: MessagesArea renders it as tables, attribute
// charts, citation badges and source links. Reading that markup out loud is
// unlistenable ("pipe pipe dash dash", raw JSON, whole URLs), so the message is
// parsed the same way it is rendered and the non-prose blocks are handled
// separately.
//
// Tables and charts are *pointed at*, not read out: the listener is looking at
// the same screen, so a spoken line only has to tell them where to look. Read
// aloud, a six-column table is a minute of cell values nobody can follow.
//
// Each reply is spoken in the language it was answered in — English, or
// Chinese with a Taiwanese voice. See replyLanguage below for how that is known.
import {
  ContentType,
  cleanMessageText,
  parseContentBlocks,
  parseTableRow,
} from "../messageRenderer/MessageWithTableRenderer";

const CJK_RE = /[㐀-䶿一-鿿豈-﫿]/g;
const CJK_PUNCT_RE = /[、，。；：！？「」『』（）《》〈〉【】]/g;
const WORD_RE = /[A-Za-z]{3,}/g;
const MIN_SPOKEN_WORDS = 2;

// Pointers, not recitations — see the note at the top of the file.
const SPEECH = {
  en: {
    period: ".",
    endsSentence: /[.!?:;]$/,
    table: "The details are in the table on screen.",
    chart: "There's a chart on screen to help you compare.",
    squared: " squared",
    cubed: " cubed",
  },
  zh: {
    period: "。",
    endsSentence: /[.!?:;。！？：；]$/,
    table: "詳細資料請看畫面上的表格。",
    chart: "畫面上有圖表可以幫助您比較。",
    squared: "平方",
    cubed: "立方",
  },
};

const countMatches = (text, re) => (text.match(re) || []).length;

/**
 * The language a reply is spoken in: "en" or "zh".
 *
 * The widget stamps each reply with the language its toggle asked the Root
 * Agent for. Replies loaded from history carry no stamp — the Root Agent does
 * not record one — so a reply written mostly in Chinese counts as Chinese. A
 * reply with no Chinese at all is English whatever the stamp says: a Chinese
 * voice would have nothing to read.
 */
export const replyLanguage = (message) => {
  const msg = typeof message === "string" ? { text: message } : message || {};
  const text = msg.text || "";
  const han = countMatches(text, CJK_RE);
  if (han === 0) return "en";
  if (msg.language === "en" || msg.language === "zh") return msg.language;
  return han > countMatches(text, WORD_RE) ? "zh" : "en";
};

/** Close up the gaps left behind by anything stripped out ("gaps ." → "gaps."). */
const tidyPunctuation = (text) =>
  text
    .replace(/[([{]\s*[)\]}]/g, " ") // brackets left empty by a strip above
    .replace(/\s+([.,;:!?，。；：！？、])/g, "$1")
    .replace(/([.,;:!?])\1+/g, "$1")
    .replace(/\s{2,}/g, " ")
    .trim();

/**
 * Strip markdown and LaTeX noise so prose sounds natural (no "asterisk").
 * An English voice has no audio for Chinese, so an English reply loses any
 * Chinese in it; a Chinese voice reads both.
 */
const speakProseLine = (line, lang) =>
  tidyPunctuation(
    (lang === "zh"
      ? line
      : line.replace(CJK_RE, " ").replace(CJK_PUNCT_RE, " "))
      .replace(/\[\d+(?:\s*,\s*\d+)*\]/g, "") // FAQ citation markers: [1], [1, 12]
      .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1") // links/images → their label
      // Bare URLs are not worth hearing. ASCII only, so a URL followed by
      // Chinese with no space between does not take the sentence with it.
      .replace(/https?:\/\/[!-~]+/g, "")
      .replace(/^\s*#{1,6}\s+/, "") // heading marker (inline # is left alone)
      .replace(/^\s*>\s?/, "") // blockquote marker
      .replace(/\*\*/g, "") // bold markers
      .replace(/\\([a-zA-Z]+)/g, "$1") // \alpha → alpha, not "backslash alpha"
      .replace(/\^\{?2\}?(?![\d.])/g, SPEECH[lang].squared) // m^2 → m squared
      .replace(/\^\{?3\}?(?![\d.])/g, SPEECH[lang].cubed) // m^3 → m cubed
      .replace(/[_^]\{([^}]*)\}/g, " $1") // other sub/superscripts: keep the value
      .replace(/[_^]/g, " ") // stray sub/superscript markers
      .replace(/[`~|]/g, " ") // stray markdown
      .replace(/\s{2,}/g, " ")
      .trim(),
  );

/** True for a line that looks like a row of a markdown table. */
const isTableLine = (line) => line.includes("|") && line.trim().length > 1;

/** True for the `|---|:--|` separator under a table's header row. */
const isSeparatorLine = (line) => /^[\s|:-]+$/.test(line.trim());

/**
 * Whether this really is a table. A stray line of pipes is prose, and
 * announcing a table that isn't there would be worse than saying nothing.
 */
const looksLikeTable = (tableContent) => {
  const lines = tableContent
    .trim()
    .split("\n")
    .filter((line) => line.trim());
  if (lines.length === 0 || parseTableRow(lines[0]).length === 0) return false;

  return lines
    .slice(isSeparatorLine(lines[1] || "") ? 2 : 1)
    .some((line) => parseTableRow(line).length > 0);
};

/** Bullet lines read as separate thoughts, so give each one a full stop. */
const asSentence = (text, lang) =>
  text && !SPEECH[lang].endsSentence.test(text)
    ? `${text}${SPEECH[lang].period}`
    : text;

/** Model and attribute names a chart is built from. */
const vizLabels = (vizData) => [
  ...(vizData?.models || []),
  ...(vizData?.scopes?.[0]?.attributes || []).map((a) => a?.attr_name),
];

// Two ways to turn a table or chart into text.
//
// `speak` is what the listener hears: a pointer at the thing on screen.
// `content` is the words the block actually holds, used only to judge whether
// the voice has anything to work with. They have to be separate: the pointers
// are fixed sentences we inject, so counting them would make any reply look
// speakable — but ignoring the block entirely would hide the words that live
// *inside* a table, which is most of what a table reply is.
const SPEAK_BLOCKS = {
  table: (tableContent, lang) =>
    looksLikeTable(tableContent) ? SPEECH[lang].table : "",
  viz: (vizData, lang) => (vizData?.scopes?.length ? SPEECH[lang].chart : ""),
};

const CONTENT_BLOCKS = {
  table: (tableContent, lang) => speakProseLine(tableContent, lang),
  viz: (vizData, lang) =>
    vizLabels(vizData)
      .map((label) => speakProseLine(label, lang))
      .join(" "),
};

/**
 * Speak a run of prose, handling any markdown table embedded in it. FAQ answers
 * render pipe tables without the `table:` keyword, so they only turn up here.
 */
const describeTextBlock = (text, renderTable, lang) => {
  const parts = [];
  let tableLines = [];

  const flushTable = () => {
    if (tableLines.length === 0) return;
    // A single pipe line is prose that happens to contain a pipe, not a table.
    parts.push(
      tableLines.length > 1
        ? renderTable(tableLines.join("\n"), lang)
        : speakProseLine(tableLines[0], lang),
    );
    tableLines = [];
  };

  for (const line of text.split("\n")) {
    if (isTableLine(line)) {
      tableLines.push(line);
      continue;
    }
    flushTable();

    // A bullet is its own thought: strip the marker and end it with a full
    // stop so the list is heard as separate items, not one run-on sentence.
    const isBullet = /^\s*[-*]\s+/.test(line);
    const spoken = speakProseLine(line.replace(/^\s*[-*]\s+/, ""), lang);
    if (spoken) parts.push(isBullet ? asSentence(spoken, lang) : spoken);
  }
  flushTable();

  return parts.filter(Boolean).join(" ");
};

/**
 * Spoken form of one message, mirroring how MessagesArea renders it.
 *
 * Accepts a message object; a bare string is treated as a default-rendered
 * reply. Only messages that MessagesArea hands to MessageRenderer get the
 * source-link stripping and `table:` / `viz:` block parsing — FAQ replies use
 * their own renderer, which does neither, and running that parse on them would
 * drop text that is visibly on screen.
 *
 * `blocks` decides what a table or chart turns into — see SPEAK_BLOCKS and
 * CONTENT_BLOCKS. `lang` is the voice's language, from replyLanguage.
 */
const buildSpeech = (message, blocks, lang) => {
  const msg = typeof message === "string" ? { text: message } : message || {};
  const text = msg.text || "";
  if (!text.trim()) return "";

  const usesBlockRenderer = !msg.isFaqAgent;

  const parsed = usesBlockRenderer
    ? parseContentBlocks(cleanMessageText(text))
    : [{ type: ContentType.TEXT, content: text }];

  return parsed
    .map((block) => {
      if (block.type === ContentType.VIZ) return blocks.viz(block.content, lang);
      if (block.type === ContentType.TABLE)
        return blocks.table(block.content, lang);
      return describeTextBlock(block.content, blocks.table, lang);
    })
    .filter(Boolean)
    .join(" ")
    .replace(/\s{2,}/g, " ")
    .trim();
};

export const toSpeech = (message) =>
  buildSpeech(message, SPEAK_BLOCKS, replyLanguage(message));

/**
 * Whether this message has anything its voice can say.
 *
 * Counts what the reply itself carries — prose, table cells and chart labels
 * alike — so a table with little or no prose around it is still offered. The
 * spoken pointers are excluded, since they are there whatever the reply says.
 * An English reply needs a couple of English words once its Chinese is
 * stripped; a Chinese reply needs Chinese, which replyLanguage already checked.
 */
export const isSpeakable = (message) => {
  const lang = replyLanguage(message);
  const content = buildSpeech(message, CONTENT_BLOCKS, lang);
  return lang === "zh"
    ? countMatches(content, CJK_RE) > 0
    : countMatches(content, WORD_RE) >= MIN_SPOKEN_WORDS;
};
